import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { disconnectSocket } from '../api/socket';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const fetchProfile = useCallback(async () => {
        if (!localStorage.getItem("token")) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const data = await api.getProfile();
            setUser(data);
        } catch (err) {
            localStorage.removeItem('token');
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProfile();

        const onLogout = () => {
            setUser(null);
            localStorage.removeItem('token');
            disconnectSocket();
        };
        window.addEventListener('auth:logout', onLogout);
        return () => window.removeEventListener('auth:logout', onLogout);
    }, [fetchProfile]);

    const login = async (email, password) => {
        const data = await api.login(email, password);
        api.setToken(data.token);
        await fetchProfile();
        return data;
    };

    const register = async (email, password, username) => {
        return api.register(email, password, username);
    };

    const loginWithGoogle = async () => {
        const { signInWithGoogle } = await import('../api/firebase');
        const idToken = await signInWithGoogle();
        const data = await api.googleLogin(idToken);
        api.setToken(data.token);
        await fetchProfile();
        return data;
    };

    const logout = async () => {
        try {
            await api.logout();
        } catch {
            // Ignore logout errors
        }
        api.clearToken();
        disconnectSocket();
        setUser(null);
    };

    const refreshProfile = async () => {
        await fetchProfile();
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, refreshProfile }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
